import { DocumentTextIcon } from "@heroicons/react/24/outline";
import React, { useMemo } from "react";
import FlagOutlineIcon from "../shared-components/icons/FlagOutlineIcon";
import { ArticleActions, ItemWithMetaData } from "../api/Items";

import ButtonWebcontentEdit from "./ButtonWebContentEdit";
import ButtonWebcontentRemove from "./ButtonWebContentRemove";
import ButtonWebcontentAdd from "./ButtonWebContentAdd";
import ButtonWebContentAddBack from "./ButtonWebContentAddBack";
import { classNames, formattedDate, atLeastOneItem } from "../utils";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import { MatchingEntitiesConfirm } from "../profiles/ProfileRowConfirm";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";
import PostArticleTranslation from "../posts/PostArticleTranslation";
import KeywordBadges from "../shared-components/KeywordBadges";

type Props = {
  item: ItemWithMetaData;
  articleAction: ArticleActions;
  isReadOnly?: boolean;
  isAmendment?: boolean;
  handleAction: (
    action: "edit" | "remove" | "add" | "cancel" | "completed" | "selected",
    values?: string[] | boolean
  ) => void;
  behaviors?: string[];
  hide?: boolean;
};

const WebContentItem = ({
  item,
  articleAction,
  isReadOnly,
  isAmendment,
  handleAction,
  behaviors,
  hide,
}: Props) => {
  const content = item.item.content;
  const score = (+(content?.score ?? 0)).toFixed(2);
  const showActions = articleAction !== ArticleActions.REMOVED;
  const showAdd = articleAction === ArticleActions.REVIEW;
  const showAddBack = articleAction === ArticleActions.REMOVED;
  const { behaviorMappingActions } = useFamaFlags();

  const flags = item.rating.filter(
    (rating) => rating.score_type === "behavior"
  );

  const formattedTextSegments = useMemo(() => {
    const getHighlights = (text: string, title: string) => {
      if (!text) return { title: [], text: [] };
      const allHighlights: [number, number][] = [];

      const extractHighlightsFromEntity = (entity: any) => {
        if (entity.value_type !== "json" || !entity.value) return;

        try {
          const parsedValue = JSON.parse(entity.value);
          if (!parsedValue.entities) return;

          parsedValue.entities.forEach((entityData: any[]) => {
            const highlights = entityData[1];
            highlights.forEach((highlight: any) => {
              if (!highlight.highlight) return;

              highlight.highlight.forEach((h: [number, number]) => {
                allHighlights.push(h);
              });
            });
          });
        } catch (e) {
          console.error("Failed to parse entity value:", e);
        }
      };

      // Extract highlights from all entities
      item.entities?.forEach(extractHighlightsFromEntity);

      const sortedHighlights = allHighlights.sort((a, b) => a[0] - b[0]);
      const titleLength = title?.length ?? 0;
      const spaceOffset = titleLength > 0 ? 1 : 0; // Account for space after title

      // Split highlights into title and text segments
      const titleHighlights: [number, number][] = [];
      const textHighlights: [number, number][] = [];

      sortedHighlights.forEach(([start, end]) => {
        // Handle highlights that start in title
        if (start < titleLength) {
          // If highlight ends in title
          if (end <= titleLength) {
            titleHighlights.push([start, end]);
          } else {
            // If highlight spans title and text
            titleHighlights.push([start, titleLength]);
            textHighlights.push([0, end - titleLength - spaceOffset]);
          }
        } else {
          // Handle highlights that start in text
          textHighlights.push([
            start - titleLength - spaceOffset,
            end - titleLength - spaceOffset,
          ]);
        }
      });

      const processSegments = (
        text: string,
        highlights: [number, number][]
      ) => {
        const result = [];
        let lastIndex = 0;

        highlights.forEach(([start, end]) => {
          if (lastIndex < start) {
            result.push({
              text: text.slice(lastIndex, start),
              highlight: false,
            });
          }
          result.push({
            text: text.slice(start, end),
            highlight: true,
          });
          lastIndex = end;
        });

        if (lastIndex < text.length) {
          result.push({
            text: text.slice(lastIndex),
            highlight: false,
          });
        }

        return result;
      };

      return {
        title: processSegments(title || "", titleHighlights),
        text: processSegments(text, textHighlights),
      };
    };

    return getHighlights(content.text, content.title);
  }, [content.text, content.title, item.entities]);

  return (
    <div
      className={classNames(
        "flex border border-gray-400 rounded-md p-4 pl-2 my-4",
        hide ? "hidden" : ""
      )}
    >
      {articleAction === ArticleActions.REVIEW && (
        <div className="pr-3 pt-3">
          <LabeledCheckbox
            name={""}
            onChange={(evt) => handleAction("selected", evt.target.checked)}
          ></LabeledCheckbox>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div>
          <div className="flex items-center">
            <div className="w-full flex items-center">
              <div className="w-6 p-1 rounded-full bg-slate-400 text-white mr-3">
                <DocumentTextIcon className="w-4"></DocumentTextIcon>
              </div>
              <div className="max-w-lg truncate">
                {content.title ? (
                  <div className="text-sm">
                    {formattedTextSegments.title.map((part, i) => (
                      <span
                        key={i}
                        className={
                          part.highlight
                            ? "bg-yellow-200 underline font-bold"
                            : ""
                        }
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                ) : (
                  content.url
                )}
              </div>

              {content.created_at && (
                <div className="text-sm text-gray-500 ml-3">
                  {formattedDate(content.created_at)}
                </div>
              )}
              {!isReadOnly && showActions && (
                <div className="ml-auto flex gap-3">
                  <ButtonWebcontentRemove
                    handleAction={handleAction}
                    item={item}
                  />
                  {behaviors && (
                    <ButtonWebcontentEdit
                      handleAction={handleAction}
                      item={item}
                      behaviors={behaviors}
                      isAmendment={isAmendment}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full truncate border-b border-gray-400 mb-3 pb-2">
            <a
              href={content.url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 ml-9"
            >
              {content.url}
            </a>
          </div>
        </div>
        <div className="text-xs text-gray-500 mb-3 text-justify break-words">
          {formattedTextSegments.text.map((part, i) => (
            <span
              key={i}
              className={
                part.highlight ? "bg-yellow-200 underline font-bold" : ""
              }
            >
              {part.text}
            </span>
          ))}
        </div>

        {(flags ?? []).length > 0 && (
          <div className="text-gray-500 flex gap-3">
            <FlagOutlineIcon />
            {flags.map((flag) => (
              <span className="flex items-center rounded-md text-xs px-3 bg-blue-100 text-gray-600">
                {behaviorMap(flag.name, behaviorMappingActions, "key")}
              </span>
            ))}
          </div>
        )}
        {item.keywords && atLeastOneItem(item.keywords) && (
          <KeywordBadges keywords={item.keywords} />
        )}
        <PostArticleTranslation item={item} />
        {(item.entities ?? []).length > 0 && (
          <MatchingEntitiesConfirm item={item} />
        )}
        <div className="flex justify-between items-center mt-2">
          {score !== "0.00" && (
            <div className="font-bold">
              Relevance Score
              <span className="text-green-500 ml-2">{score}</span>
            </div>
          )}
          {!isReadOnly && showAdd && (
            <div className="flex justify-end ml-auto">
              <ButtonWebcontentAdd
                handleAction={handleAction}
                item={item}
                behaviors={behaviors}
              />
            </div>
          )}
          {!isReadOnly && showAddBack && (
            <div className="flex justify-end ml-auto">
              <ButtonWebContentAddBack
                handleAction={handleAction}
                item={item}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebContentItem;
